import { Info } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { resolveDateLocale } from "./calendar-helpers";
import Tooltip from "./Tooltip";

export interface RangePreviewProps {
  createdCount: number | null | undefined;
  excludedDates?: string[];
  isRangeEnabled?: boolean;
  hasEndDate?: boolean;
  className?: string;
}

export function RangePreview({
  createdCount,
  excludedDates = [],
  isRangeEnabled = false,
  hasEndDate = false,
  className = "",
}: RangePreviewProps) {
  const { t, i18n } = useTranslation(["timeEntries", "common"] as const);
  const dateLocale = resolveDateLocale(i18n.resolvedLanguage);
  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.resolvedLanguage),
    [i18n.resolvedLanguage],
  );

  const compactFormat = useCallback(
    (iso: string) => {
      try {
        return format(parseISO(iso), "dd/MM", { locale: dateLocale });
      } catch {
        return iso;
      }
    },
    [dateLocale],
  );
  const formattedExcludedDates = excludedDates.map(compactFormat);
  const compactExcludedText = formattedExcludedDates.join(", ");
  const totalExcluded = excludedDates.length;
  const maxExcludedChars = 15;
  const shouldTruncateExcluded = compactExcludedText.length > maxExcludedChars;

  const { visibleExcludedText, hiddenExcludedCount } = useMemo(() => {
    if (!shouldTruncateExcluded) {
      return {
        visibleExcludedText: compactExcludedText,
        hiddenExcludedCount: 0,
      };
    }

    const visibleItems: string[] = [];
    for (const dateText of formattedExcludedDates) {
      const nextValue =
        visibleItems.length === 0
          ? dateText
          : `${visibleItems.join(", ")}, ${dateText}`;
      if (nextValue.length > maxExcludedChars) {
        break;
      }
      visibleItems.push(dateText);
    }

    const fallbackText = compactExcludedText.slice(0, maxExcludedChars);
    const finalVisibleText =
      visibleItems.length > 0 ? visibleItems.join(", ") : fallbackText;

    return {
      visibleExcludedText: finalVisibleText,
      hiddenExcludedCount: Math.max(
        formattedExcludedDates.length - visibleItems.length,
        0,
      ),
    };
  }, [
    compactExcludedText,
    formattedExcludedDates,
    maxExcludedChars,
    shouldTruncateExcluded,
  ]);

  if (!isRangeEnabled || !hasEndDate) {
    return null;
  }

  const totalToCreate = createdCount ?? 0;
  const formattedTotal = numberFormatter.format(totalToCreate);

  return (
    <div className={`w-full ${className}`}>
      <div className="text-sm font-medium text-content-secondary">
        {t("rangePreview.create", {
          count: totalToCreate,
          formatted: formattedTotal,
        })}
      </div>
      {totalExcluded > 0 ? (
        <div className="mt-1 text-xs leading-5 text-content-muted">
          <span className="inline-flex items-center gap-1 font-medium text-content-muted">
            <span>
              {t("rangePreview.excludedLabel", { count: totalExcluded })}:
            </span>
            <Tooltip
              content={t("rangePreview.excludedReason", {
                count: totalExcluded,
              })}
            >
              <span aria-label="excluded-reason-info" className="inline-flex">
                <Info size={12} className="text-content-muted" />
              </span>
            </Tooltip>
          </span>{" "}
          <span>{visibleExcludedText}</span>
          {hiddenExcludedCount > 0 ? (
            <>
              {" "}
              <Tooltip content={compactExcludedText}>
                <span
                  aria-label="excluded-more-info"
                  className="inline cursor-help text-content-muted underline decoration-dotted"
                >
                  {t("rangePreview.moreSuffix", { count: hiddenExcludedCount })}
                </span>
              </Tooltip>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default RangePreview;
